/** Send a contracted quote — recipient entry, the designated owner, PDF attached. */
import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, X, Loader2, UserCheck, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { API, useAuth } from "@/App";

export const SendQuoteDialog = ({ quote, token, onClose, onSent }) => {
  const [form, setForm] = useState({
    recipient_name: "", recipient_title: "", recipient_org: quote.customer_name || "",
    to: "", cc: "", note: "",
  });
  const [sending, setSending] = useState(false);
  const [blocked, setBlocked] = useState("");
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const send = async (override = false) => {
    const to = form.to.split(/[,;\s]+/).filter((e) => e.includes("@"));
    if (!to.length) return toast.error("Add at least one recipient email");
    if (!form.recipient_name.trim()) return toast.error("Who is this going to? Add their name");
    setSending(true);
    try {
      const r = await axios.post(`${API}/admin/capital-quotes/${quote.id}/send`, {
        to_emails: to,
        cc_emails: form.cc.split(/[,;\s]+/).filter((e) => e.includes("@")),
        recipient_name: form.recipient_name.trim(),
        recipient_title: form.recipient_title.trim(),
        recipient_org: form.recipient_org.trim(),
        note: form.note.trim(),
        ...(override ? { override_open_questions: true } : {}),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Quote ${quote.quote_number} sent to ${to.join(", ")}`, {
        description: r.data.pdf_attached ? "PDF attached and the live link included."
          : "Link included — the PDF could not be attached.",
      });
      onSent?.();
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail || "Could not send the quote";
      if (e.response?.status === 409) setBlocked(detail);
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/45 flex items-center justify-center p-3"
      onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="send-quote-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] font-bold text-blue-700">{quote.quote_number}</p>
            <h3 className="text-base font-bold text-slate-900">Send this quote</h3>
            <p className="text-[12px] text-slate-500">
              The PDF is attached and the live link lets them ask questions against the scope.
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"
            data-testid="send-quote-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-5 space-y-3">
          <div className="rounded-lg bg-blue-50 border border-blue-200 px-3 py-2 text-[12px] text-blue-900"
            data-testid="send-quote-owner">
            <UserCheck className="w-3.5 h-3.5 inline mr-1.5" />
            <strong>Owner of record:</strong> {quote.owner_name || "George Broughton III"}
            {quote.owner_email ? ` · ${quote.owner_email}` : ""}
            <span className="block text-blue-800/80 mt-0.5">
              Their signature block and contact details go out with the quote; replies come back to them.
            </span>
          </div>

          {blocked && (
            <div className="rounded-lg bg-amber-50 border border-amber-300 px-3 py-2 text-[12px] text-amber-900"
              data-testid="send-quote-blocked">
              <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
              {blocked}
              <span className="block mt-0.5 text-amber-800/90">
                {isSuper
                  ? "As super admin you can override and send anyway — the override is logged on the quote."
                  : "Ask a super admin to override, or publish the answers first."}
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                Recipient name *
              </label>
              <Input value={form.recipient_name} onChange={(e) => set("recipient_name", e.target.value)}
                placeholder="Ray Hernandez" data-testid="send-quote-name" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
                Title
              </label>
              <Input value={form.recipient_title} onChange={(e) => set("recipient_title", e.target.value)}
                placeholder="Director of Facilities" data-testid="send-quote-title" />
            </div>
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              Organisation
            </label>
            <Input value={form.recipient_org} onChange={(e) => set("recipient_org", e.target.value)}
              data-testid="send-quote-org" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              To * <span className="text-slate-400 normal-case font-normal">comma separated</span>
            </label>
            <Input value={form.to} onChange={(e) => set("to", e.target.value)}
              placeholder="facilities@harrishealth.org" data-testid="send-quote-to" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              CC
            </label>
            <Input value={form.cc} onChange={(e) => set("cc", e.target.value)}
              placeholder="engineering@harrishealth.org" data-testid="send-quote-cc" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 block mb-1">
              Covering note
            </label>
            <Textarea rows={3} value={form.note} onChange={(e) => set("note", e.target.value)}
              placeholder="As discussed on site Tuesday — the turn-key scope with the two-day shutdown window."
              data-testid="send-quote-note" />
          </div>
        </div>

        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {blocked && isSuper && (
            <Button onClick={() => send(true)} disabled={sending}
              className="bg-amber-600 hover:bg-amber-700" data-testid="send-quote-override">
              <AlertTriangle className="w-4 h-4 mr-1.5" /> Send anyway
            </Button>
          )}
          <Button onClick={() => send(false)} disabled={sending} className="bg-blue-600 hover:bg-blue-700"
            data-testid="send-quote-submit">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Send className="w-4 h-4 mr-1.5" />}
            Send quote
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SendQuoteDialog;
